import React, { useLayoutEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Lottie from "lottie-react";
import "./KycMobileClientSections.css";
import { MOBILE_TEXT_ENTRY_PROPS } from "./mobileTextEntryProps";
import pepAnim from "../../../images/pep-lottie.json";
import sanctionsAnim from "../../../images/sanctions-lottie.json";

function autoResizeTextarea(el) {
  if (!el) return;
  const computed = window.getComputedStyle(el);
  const lineHeight = parseFloat(computed.lineHeight) || 24;
  const paddingTop = parseFloat(computed.paddingTop) || 0;
  const paddingBottom = parseFloat(computed.paddingBottom) || 0;
  const borderTop = parseFloat(computed.borderTopWidth) || 0;
  const borderBottom = parseFloat(computed.borderBottomWidth) || 0;
  const verticalExtras = paddingTop + paddingBottom + borderTop + borderBottom;
  const minHeight = lineHeight * 3 + verticalExtras;
  const maxHeight = lineHeight * 10 + verticalExtras;

  el.style.height = "auto";
  const contentHeight = el.scrollHeight;
  const nextHeight = Math.min(Math.max(contentHeight, minHeight), maxHeight);
  el.style.height = `${nextHeight}px`;
  el.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden";
}

const DETAIL_TEXTAREA_MIN_HEIGHT = "calc(1.45em * 3 + 1.1rem)";

function InlineDangerMessage({ children }) {
  return (
    <div className="kycMobileFieldError">
      <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />{" "}
      {children}
    </div>
  );
}

export default function KycMobilePepSanctions({
  form,
  onSetAt,
  readOnly = false,
  continueAttempted = false,
}) {
  const pep = form?.pepSanctions || {};
  const isEntity = form?.customer?.type === "entity";
  const pepDetailsRef = useRef(null);
  const sanctionsDetailsRef = useRef(null);
  const missingPepDeclaration =
    continueAttempted && typeof pep.pepDeclared !== "boolean";
  const missingSanctionsDeclaration =
    continueAttempted && typeof pep.sanctionsDeclared !== "boolean";

  useLayoutEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      autoResizeTextarea(pepDetailsRef.current);
      autoResizeTextarea(sanctionsDetailsRef.current);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [pep.pepDetails, pep.sanctionsDetails, pep.pepDeclared, pep.sanctionsDeclared]);

  function setPep(pathTail, value) {
    onSetAt?.(["pepSanctions", ...pathTail], value);
  }

  return (
    <div className="kycMobileSectionStack">
      <div className="kycMobileQuestionCard">
        <div className="kycMobileQuestionRail">
          <div className="kycMobileQuestionLottieWrap">
            <Lottie
              animationData={pepAnim}
              loop
              autoplay
              className="kycMobileQuestionLottie"
            />
          </div>
        </div>
        <div className="kycMobileQuestionContent">
          <div className="kycMobileFieldLabel kycMobileFieldLabel--question">
            {isEntity
              ? "Does any beneficial owner, controlling person, authorised signatory, director, or other person connected with the entity hold, or have they ever held, a prominent public function, or are they a close family member or close associate of a person who does or has held such a function?"
              : "Does the individual hold, or have they ever held, a prominent public function, or are they a close family member or close associate of a person who does or has held such a function?"}
          </div>
          <div className="kycMobileSegment">
            <button
              type="button"
              className={`kycMobileSegmentBtn ${
                pep.pepDeclared === false ? "is-active" : ""
              }`}
              onClick={() => setPep(["pepDeclared"], false)}
              disabled={readOnly}
            >
              No
            </button>
            <button
              type="button"
              className={`kycMobileSegmentBtn ${
                pep.pepDeclared === true ? "is-active" : ""
              }`}
              onClick={() => setPep(["pepDeclared"], true)}
              disabled={readOnly}
            >
              Yes
            </button>
          </div>
          {missingPepDeclaration ? (
            <InlineDangerMessage>
              Select yes or no for this declaration
            </InlineDangerMessage>
          ) : null}
        </div>
      </div>

      <AnimatePresence>
        {pep.pepDeclared ? (
          <motion.div
            key="pep-details"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
              opacity: { duration: 0.22, delay: 0.1 },
            }}
            style={{ overflow: "hidden" }}
          >
            <div className="kycMobileFieldCard">
              <label className="kycMobileFieldLabel">PEP details</label>
              <textarea
                {...MOBILE_TEXT_ENTRY_PROPS}
                ref={pepDetailsRef}
                className="form-control kycMobileAutoGrowTextarea"
                rows={3}
                style={{ minHeight: DETAIL_TEXTAREA_MIN_HEIGHT }}
                value={pep.pepDetails || ""}
                onChange={(e) => {
                  autoResizeTextarea(e.currentTarget);
                  setPep(["pepDetails"], e.target.value);
                }}
                onInput={(e) => autoResizeTextarea(e.currentTarget)}
                disabled={readOnly}
                placeholder="Provide names, position, relationship, and country."
              />
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <div className="kycMobileQuestionCard">
        <div className="kycMobileQuestionRail">
          <div className="kycMobileQuestionLottieWrap">
            <Lottie
              animationData={sanctionsAnim}
              loop
              autoplay
              className="kycMobileQuestionLottie"
            />
          </div>
        </div>
        <div className="kycMobileQuestionContent">
          <div className="kycMobileFieldLabel kycMobileFieldLabel--question">
            {isEntity
              ? "Is the entity, or any beneficial owner, controlling person, authorised signatory, director, or other connected person connected with it, subject to any sanctions measures or terrorism financing restrictions?"
              : "Is the individual subject to any sanctions measures or terrorism financing restrictions?"}
          </div>
          <div className="kycMobileSegment">
            <button
              type="button"
              className={`kycMobileSegmentBtn ${
                pep.sanctionsDeclared === false ? "is-active" : ""
              }`}
              onClick={() => setPep(["sanctionsDeclared"], false)}
              disabled={readOnly}
            >
              No
            </button>
            <button
              type="button"
              className={`kycMobileSegmentBtn ${
                pep.sanctionsDeclared === true ? "is-active" : ""
              }`}
              onClick={() => setPep(["sanctionsDeclared"], true)}
              disabled={readOnly}
            >
              Yes
            </button>
          </div>
          {missingSanctionsDeclaration ? (
            <InlineDangerMessage>
              Select yes or no for this declaration
            </InlineDangerMessage>
          ) : null}
        </div>
      </div>

      <AnimatePresence>
        {pep.sanctionsDeclared ? (
          <motion.div
            key="sanctions-details"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
              opacity: { duration: 0.22, delay: 0.1 },
            }}
            style={{ overflow: "hidden" }}
          >
            <div className="kycMobileFieldCard">
              <label className="kycMobileFieldLabel">Sanctions details</label>
              <textarea
                {...MOBILE_TEXT_ENTRY_PROPS}
                ref={sanctionsDetailsRef}
                className="form-control kycMobileAutoGrowTextarea"
                rows={3}
                style={{ minHeight: DETAIL_TEXTAREA_MIN_HEIGHT }}
                value={pep.sanctionsDetails || ""}
                onChange={(e) => {
                  autoResizeTextarea(e.currentTarget);
                  setPep(["sanctionsDetails"], e.target.value);
                }}
                onInput={(e) => autoResizeTextarea(e.currentTarget)}
                disabled={readOnly}
                placeholder="Explain the relevant sanctions exposure."
              />
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
